// @flow

import _ from 'lodash';

import type {
    CalculationRequestInfo,
    CalculationRequestProjectInput,
    CalculationRequestProjectResult,
    UserClaim,
    AuthSummary
} from "../common/RequestTypes";
import type {PhaseState} from '../components/CalculationPhaseInput'
import DefaultValueManager from "../common/DefaultValueManager";
import OptionManager from "../common/OptionManager";
import { history } from '../index';
import Config from "../common/Config";
import ReactAI from 'react-appinsights';
import config from "../common/Config";


type UserAuth = {
    +user_id: string,
    +user_claims: UserClaim[],
    +provider_name: string
}

export type ProjectUiInput = {
    +touched: {[name:string]: boolean},
    +error: {[name:string]: ?string},

    +projectName: string,
    +personName: string,
    +organisationName: string,
    
    +phaseStates: PhaseState[],

    +version: string,
    
}

type CalculationReduxState = {

    +projectUiInput: ProjectUiInput,
    
    +calcInput: ?CalculationRequestProjectInput,
    +calcResult: ?CalculationRequestProjectResult,
    
    +calcErrorCode: number,
    +calcErrorString: string,
    
    +isLoading: boolean,

    +userAuths: ?UserAuth[],
    +userAuthsIsLoading: boolean,
    
    +adminHistory: ?CalculationRequestInfo[],
    +adminHistoryIsLoading: boolean,
    +allAdminHistoryLoaded: boolean,

    +history: ?CalculationRequestInfo[],
    +historyIsLoading: boolean,
    +allHistoryLoaded: boolean,
    
    personEmail: string,
    authSummary: AuthSummary[]
};


type ChangePhaseValuesAction = {
    type: 'CHANGE_PHASE_VALUES',
    phaseNr: number,
    partialPhaseState: Object,
}

type AddPhaseAction = {
    type: 'ADD_PHASE',
}

type RemovePhaseAction = {
    type: 'REMOVE_PHASE',
    phaseNr: number,
}

type RequestCalculationAction = {
    type: 'REQUEST_CALCULATION',
    calcInput: CalculationRequestProjectInput
}

type ReceiveCalculationAction = {
    type: 'RECEIVE_CALCULATION',
    calcInput: CalculationRequestProjectInput,
    calcResult: CalculationRequestProjectResult
}

type ReceiveCalculationErrorAction = {
    type: 'RECEIVE_CALCULATION_ERROR',
    errorCode: number,
    errorString: string
}

type ClearCalculationResultAction = {
    type: 'CLEAR_CALCULATION_RESULT',
}

type SetCalcParamsAction = {
    type: 'SET_CALC_PARAMS',
    params: ProjectUiInput,
}

type RequestUserAuth = {
    type: 'REQUEST_USER_AUTH',
}

type ReceiveUserAuth = {
    type: 'RECEIVE_USER_AUTH',
    userAuths: UserAuth[],
}

type RequestHistory = {
    type: 'REQUEST_HISTORY',
}

type ReceiveHistory = {
    type: 'RECEIVE_HISTORY',
    history: CalculationRequestInfo[],
}

type AddToHistoryAction = {
    type: 'ADD_TO_HISTORY',
    calcRequestInfo: CalculationRequestInfo,
}

type AllHistoryLoadedAction = {
    type: 'ALL_HISTORY_LOADED',
}

type RequestAdminHistory = {
    type: 'REQUEST_ADMIN_HISTORY',
}

type ReceiveAdminHistory = {
    type: 'RECEIVE_ADMIN_HISTORY',
    adminHistory: CalculationRequestInfo[],
}

type AddToAdminHistoryAction = {
    type: 'ADD_TO_ADMIN_HISTORY',
    calcRequestInfo: CalculationRequestInfo,
}

type AllAdminHistoryLoadedAction = {
    type: 'ALL_ADMIN_HISTORY_LOADED',
}


export type Action = SetCalcParamsAction 
    | AddPhaseAction 
    | RemovePhaseAction 
    | ChangePhaseValuesAction 
    | RequestCalculationAction 
    | ReceiveCalculationAction 
    | ClearCalculationResultAction 
    | ReceiveCalculationErrorAction
    | RequestUserAuth
    | ReceiveUserAuth
    | RequestHistory
    | ReceiveHistory
    | AddToHistoryAction
    | AllHistoryLoadedAction
    | RequestAdminHistory
    | ReceiveAdminHistory
    | AddToAdminHistoryAction
    | AllAdminHistoryLoadedAction;

/* eslint-disable no-use-before-define */
type GetState = () => CalculationReduxState;
type PromiseAction = Promise<Action>;
type ThunkAction = (dispatch: Dispatch, getState: GetState) => any;
type Dispatch = (action: Action | ThunkAction | PromiseAction | Array<Action>) => any;
/* eslint-enable no-use-before-define */

const changePhaseValuesType = 'CHANGE_PHASE_VALUES';
const addPhaseType = 'ADD_PHASE';
const removePhaseType = 'REMOVE_PHASE';
const requestCalculationType = 'REQUEST_CALCULATION';
const receiveCalculationType = 'RECEIVE_CALCULATION';
const receiveCalculationErrorType = 'RECEIVE_CALCULATION_ERROR';
const clearCalculationResultType = 'CLEAR_CALCULATION_RESULT';
const setCalcParamsType = 'SET_CALC_PARAMS';
const requestUserAuthType = 'REQUEST_USER_AUTH';
const receiveUserAuthType = 'RECEIVE_USER_AUTH';
const requestHistoryType = 'REQUEST_HISTORY';
const requestAdminHistoryType = 'REQUEST_ADMIN_HISTORY';
const receiveHistoryType = 'RECEIVE_HISTORY';
const receiveAdminHistoryType = 'RECEIVE_ADMIN_HISTORY';
const addToHistoryType = 'ADD_TO_HISTORY';
const addToAdminHistoryType = 'ADD_TO_ADMIN_HISTORY';
const allHistoryLoadedType = 'ALL_HISTORY_LOADED';
const allAdminHistoryLoadedType = 'ALL_ADMIN_HISTORY_LOADED';

const optionManager = new OptionManager();
const defaultValueManager = new DefaultValueManager(optionManager);

const initialState: CalculationReduxState = { calcInput: null, calcResult: null, isLoading: false, calcErrorCode: 0, calcErrorString: "",
    projectUiInput: defaultValueManager.getDefaultProjectState(), userAuths: null, userAuthsIsLoading: false, 
    history: null, historyIsLoading: false, allHistoryLoaded: false, 
    adminHistory: null, adminHistoryIsLoading: false, allAdminHistoryLoaded: false, 
    personEmail: null, authSummary: null
}; 


export const actionCreators = {

    changePhaseValues(phaseNr: number, partialPhaseState: Object) : ChangePhaseValuesAction {
        return {
            type: changePhaseValuesType,
            phaseNr,
            partialPhaseState,
        };
    },

    addPhase() : AddPhaseAction {
        return {
            type: addPhaseType,
        };
    },

    removePhase(phaseNr: number) : RemovePhaseAction {
        return {
            type: removePhaseType,
            phaseNr
        };
    },

    clearCalculationResult() : ClearCalculationResultAction {
        return {
            type: clearCalculationResultType
        };
    },

    setCalcParams(params: ProjectUiInput) : SetCalcParamsAction {
        return {
            type: setCalcParamsType,
            params
        };
    },

    requestCalculation: (calcInput:CalculationRequestProjectInput) => async (dispatch: Dispatch, getState: GetState) => {
        
        // TODO: Check for duplicate requests?
        
        const authSummary = getState().calculation.authSummary;
        calcInput.authSummary = authSummary;
        
        dispatch({ type: requestCalculationType, calcInput });

        // Use to simulate network error
        // const url = `https://provemnnnnnt.se/api/Calculation/Calculate`;
        const url = Config.urlCalcProject;

        try {

            let bodyString = JSON.stringify(calcInput);
            // TODO: Remove when debugging finished
            console.log(`Sending request ${bodyString}`);
            
            const rawResponse = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: bodyString
            });

            if (rawResponse.ok) {
                let calcResult = await rawResponse.json();
                calcResult = {
                    ...calcResult,
                    phaseResults: calcResult.phaseResults.map((phaseResult, idx) => 
                        ({...phaseResult, techniquesToCompare: calcInput.phases[idx].techniquesToCompareUiCode}))
                };
                let receiveCalcAction: ReceiveCalculationAction = {type: receiveCalculationType, calcInput, calcResult};
                dispatch(receiveCalcAction);

                let calcRequestInfo = createCalcRequestInfo(receiveCalcAction);
                dispatch({type: addToHistoryType, calcRequestInfo});
                dispatch({type: addToAdminHistoryType, calcRequestInfo});
                
                history.push('/result');
                window.scrollTo(0, 0);
            } else {
                // Network errors not handled here, this is for example http 404 or 500 error response
                let responseText = await rawResponse.text();
                let errorMsg = `Received error response from calculation service. Status code ${rawResponse.status}. Response text: ${responseText}`;
                console.error(errorMsg);
                // alert(errorMsg);
                dispatch({
                    type: receiveCalculationErrorType,
                    errorCode: rawResponse.status,
                    errorString: responseText
                });
            }
            
        } catch(e) {
            // Network error for example
            let errorMsg = `Probable network error when calling calculation service. Exception: ${e.toString()}`;
            // alert(errorMsg);
            console.error(errorMsg);
            dispatch({
                type: receiveCalculationErrorType,
                errorCode: -1,
                errorString: e.toString()
            });
        }
            
        
    },

    requestUserAuth: () => async (dispatch: Dispatch, getState: GetState) => {

        if(config.simulateLoggedInUser) {
            let authSampleResponseNcc = `[{"access_token":"AQABAAAAAAC5una0EUFgTIF8ElaxtWjTq_mBuiAepZLdfbQ96v01emiH3J38I5xGy1ewo0PI-qCx_ttZuuBI_K8OUOJig5S90wLcpsTVZl-i03zzAmSXhxnlQX_xI35f3M1TRuHOtXtwuDscnSYRX1T0y-Ujmt5wOtCtAHya8uPoTo3HfKlXSK8eYTISayaZ-grD377AVnzjgBGSYTgvHAFajOBp95uvkharOjs4mvJ_huqYVxo2PD5LZMsOFFQ6-59hrpMh_viPlL5d7wdctu6r5I4NPWLKqAx64vNRofJTqJ69wOGJDoaPtwHxK9AE2Dsc4LYlXfFaeckXwzciG720GwdJaZxYcnZwC_ArgUGZXM06EyQwrz1CipSyXXAvGZa98AabVpGKm8z6tCIlxMTGy1H7GYqzmwu5nzozukqkpG0kKtJofVfw2dfsLXbQ_W2z8ift-r86JvSynIowCg4PhrywjQvGBvia8huSXTsJx7LF3oDf-ZNMf1fGrR993e_Y5pcaIWA0DG1nYSHY-HmfMhVRdsOJ9vi6L0f4_4fYShPQVXADEj-g2n9aMeJdyxxBfhcQZ24AJTSdaq1a8SLxojJ-GTYP49MaMNU0xAqqO0lUB2ZXP5_6kbjL2QSOMFUNS7sqycJM0eE0xDuntkaKqnxTGqmpIAA","expires_on":"2018-10-11T07:47:59.0000000Z","id_token":"eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsIng1dCI6Imk2bEdrM0ZaenhSY1ViMkMzbkVRN3N5SEpsWSIsImtpZCI6Imk2bEdrM0ZaenhSY1ViMkMzbkVRN3N5SEpsWSJ9.eyJhdWQiOiIzNzMzMmJhNy0wOWIwLTQyZDMtODU3OS03MDc5OTdmNGU3ZTQiLCJpc3MiOiJodHRwczovL3N0cy53aW5kb3dzLm5ldC9kMDQ0OTlkNS0yODU3LTRlMjgtOTE3Ny02ZTk0OTg1OWRiODMvIiwiaWF0IjoxNTM5MjQwMTc5LCJuYmYiOjE1MzkyNDAxNzksImV4cCI6MTUzOTI0NDA3OSwiYWlvIjoiNDJSZ1lQQU8vc0ZSVzdMa1orYitTZnAyNHZzVWVBdm5icmp3ekRxancrdEIwZUsraThrQSIsImFtciI6WyJwd2QiXSwiZmFtaWx5X25hbWUiOiJXaW5iZXJnIiwiZ2l2ZW5fbmFtZSI6IlJpY2thcmQiLCJpcGFkZHIiOiIxNTguMTc0LjgyLjM2IiwibmFtZSI6IldpbmJlcmcgUmlja2FyZCIsIm5vbmNlIjoiY2Y4MDRlMTdiMTVlNDA1Yjg2ODk3ZjIxOTU3OTVkZmRfMjAxODEwMTEwNjUyNTgiLCJvaWQiOiJiYzg2YTNkZC1kNDQwLTQxYmMtYmNiMS1mYzFkOTgwODBhNDciLCJvbnByZW1fc2lkIjoiUy0xLTUtMjEtMTA3ODA4MTUzMy0xMzAzNjQzNjA4LTY4MjAwMzMzMC0xMjE5OTUiLCJzdWIiOiJFSDBPYXBxT1FSVXljaFNzb0xkeEpqWk1DR2pHNzFEMXhZMlBocFh3dEFFIiwidGlkIjoiZDA0NDk5ZDUtMjg1Ny00ZTI4LTkxNzctNmU5NDk4NTlkYjgzIiwidW5pcXVlX25hbWUiOiJyaWNrYXJkLndpbmJlcmdAbmNjLnNlIiwidXBuIjoicmlja2FyZC53aW5iZXJnQG5jYy5zZSIsInV0aSI6IkxEdDlRU0FNVFUtYTJtT1dPUFVQQUEiLCJ2ZXIiOiIxLjAifQ.sPBqPK3xI8vpsGSCq8cqmhYxuWBY2CN7KbbRc9u8FDAmsyb2Ya3RNpvTRIG7fWQ-N2Ncymw6AuMh3RU0ZTF80lclTO5X1imPFuapvL3HG9pZejEe1swALyrDdVgeynV5e3pPcRlAf7aJaddudSMloKMYdJr1g5OShuxrhn-gD5_4xCCVlf5W9BJxLqXa4WAr0O3DdvA0hWs_6RDQAhToHu-1nO1JpqB4GIEoQobo-5uStgsH_VMbePxAU-8_NdhUves44Sq6LCncXRZgtBvjk1DgMdPPs_dw_Rs8kFGCvvaz_lL982YEW9iIcDdCa0tEUWjIUpQRUjjGL4AiPjmXSQ","provider_name":"aad","refresh_token":"AQABAAAAAAC5una0EUFgTIF8ElaxtWjTlZEA2WW6V-jQDOECX_OEr2RSp6aUlB1V7NNwq8yjQdbTYsNesYgeS0vG-UEeckLVY0j3a-mA2l2UHUDmYmHR-lq57dTDvKfw1I8mpBux-SW2tQGYdozJ6KfOlqrotZ0erU97gGW-nB72LV-vbOJ17cPEctZvcWXxy76x9m6IqT_oC_HD1bqV6i-Wp283p6wMjyTM5nVpujRQLlZM0jPn5Xlrm_HQTBi8mdLMOLs5kF9JIfwIoeaehHDiyiTspjRCyZuhrJIfnMI9sfaWmCFs0XWBUTGuXHqp8g4bwY2dVm286d0rJssS3XnpKcG0W3HY8ZD4IKVBxUhb6GZucTsQT50rBqYYQHPfkR52AYr46cwPBte4QWw1GvVY2CWu-P12E2iRWTT-JUkDcHprkokp5ganNiVOyp-S4XPXnM4Bl9v5MgVUUPZU8G5pa_RqlS4cdAetqHwNuRyTqloKA82L6pI2hPLwkbzSyMi_zgdgRQ5VaMq5WWDBKyERLamL7nNFbhPTb2nL8Z3IgXFgmCrrd1QcIxTZEFH3U3hdTr3dM06GiCs-AU1bxmJxY67ivp0AAogf9pn7HlbzI3ljUo5cz_MaXLt-dilqLiEX6plJUrmsODqazSJ9cIm1_4rfBbF-yc-l9_QdTiJkNtqBPS1bZERU_No0_3fVHkc3n7_y-MaaGYDHy04gCNMiG3142AbpYBX8fieZWe32s_48GpUyd5szm0CBRzFlqwuCSDkC6FUpj009MrQSDJy1UCpRDru1xdx7YIXoVXBA4cNn9LM-GGXYrUX0C2Y2DXAwdGZV2CYwWbKBfjjxIMIT-vLodBFK_5L9UdJyhTOPtIeOp6W-BRPiFI0NZLp3d8EuzUjaEicCuVB3SlMD4gR1Z6kCAubEIAA","user_claims":[{"typ":"aud","val":"37332ba7-09b0-42d3-8579-707997f4e7e4"},{"typ":"iss","val":"https:\\/\\/sts.windows.net\\/d04499d5-2857-4e28-9177-6e949859db83\\/"},{"typ":"iat","val":"1539240178"},{"typ":"nbf","val":"1539240178"},{"typ":"exp","val":"1539244078"},{"typ":"aio","val":"42RgYDAI3sQmaqR44\\/sN4wuXWopFmj9HRa+wjz3R9P2BxDT3dx8A"},{"typ":"http:\\/\\/schemas.microsoft.com\\/claims\\/authnmethodsreferences","val":"pwd"},{"typ":"c_hash","val":"IdWJetT1iki-6FrqdYzRBg"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/surname","val":"Winberg"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/givenname","val":"Rickard"},{"typ":"ipaddr","val":"158.174.82.36"},{"typ":"name","val":"Winberg Rickard"},{"typ":"nonce","val":"cf804e17b15e405b86897f2195795dfd_20181011065258"},{"typ":"http:\\/\\/schemas.microsoft.com\\/identity\\/claims\\/objectidentifier","val":"bc86a3dd-d440-41bc-bcb1-fc1d98080a47"},{"typ":"onprem_sid","val":"S-1-5-21-1078081533-1303643608-682003330-121995"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/nameidentifier","val":"EH0OapqOQRUychSsoLdxJjZMCGjG71D1xY2PhpXwtAE"},{"typ":"http:\\/\\/schemas.microsoft.com\\/identity\\/claims\\/tenantid","val":"d04499d5-2857-4e28-9177-6e949859db83"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/name","val":"rickard.winberg@ncc.se"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/upn","val":"rickard.winberg@ncc.se"},{"typ":"uti","val":"VYjKbDx3n0Wd-UNOec8sAA"},{"typ":"ver","val":"1.0"}],"user_id":"rickard.winberg@ncc.se"}]`;
            let authSampleResponsePollex = `[{"access_token":"eyJ0eXAiOiJKV1QiLCJub25jZSI6Ik5yN0kwLU9SdElaQ0hHakhHVW1ZT1R3QkY0SEdUbU9WVjhZaE5WUlFNYk0iLCJhbGciOiJSUzI1NiIsIng1dCI6Ii1LSTNROW5OUjdiUm9meG1lWm9YcWJIWkdldyIsImtpZCI6Ii1LSTNROW5OUjdiUm9meG1lWm9YcWJIWkdldyJ9.eyJhdWQiOiIwMDAwMDAwMy0wMDAwLTAwMDAtYzAwMC0wMDAwMDAwMDAwMDAiLCJpc3MiOiJodHRwczovL3N0cy53aW5kb3dzLm5ldC80ZThiZGEwNS02NWRhLTQ5MDAtODc4NS1mMDQxODhjY2Y1NTYvIiwiaWF0IjoxNjc4MjE2MDE1LCJuYmYiOjE2NzgyMTYwMTUsImV4cCI6MTY3ODIyMTE2MSwiYWNjdCI6MCwiYWNyIjoiMSIsImFpbyI6IkFWUUFxLzhUQUFBQXNDMTk2VDVzSFVmS3pYMlQ2bTJXOG1NVUZ6cTFWVFVna1EyRXZRcXRHWkRDRE9xUDN5Nnk5VFFjM0QwVjdHQitvUUlabG4wclN2UHV0bjh0dmpFQWFqU2FYV0hBaVl4bjJaZ3AvbjVhalJvPSIsImFtciI6WyJwd2QiLCJtZmEiXSwiYXBwX2Rpc3BsYXluYW1lIjoiUG9sbGV4IC0gS2xpbWF0a2Fsa3lsYXRvciIsImFwcGlkIjoiODJmYjg3YjAtMGFhNC00ZTgxLWE3MTMtYmRiYTgyZjE3MTM3IiwiYXBwaWRhY3IiOiIxIiwiZmFtaWx5X25hbWUiOiJXaW5iZXJnIiwiZ2l2ZW5fbmFtZSI6IlJpY2thcmQiLCJpZHR5cCI6InVzZXIiLCJpcGFkZHIiOiI5OC4xMjguMjI2LjE3IiwibmFtZSI6IlJpY2thcmQgV2luYmVyZyIsIm9pZCI6ImVlYTEzOTZhLWQ4ZWQtNDlmZS1hNmY2LTk0NGZmN2U1MDU2ZCIsInBsYXRmIjoiNSIsInB1aWQiOiIxMDAzMjAwMjU4OUJDODc4IiwicmgiOiIwLkFUa0FCZHFMVHRwbEFFbUhoZkJCaU16MVZnTUFBQUFBQUFBQXdBQUFBQUFBQUFBNUFJVS4iLCJzY3AiOiJlbWFpbCBvcGVuaWQgcHJvZmlsZSIsInN1YiI6InNMSVpFM0t2clE5T3NLY1VVcHFyN2pSZHlzcUI3Y1lBd093dzdLNWhHam8iLCJ0ZW5hbnRfcmVnaW9uX3Njb3BlIjoiRVUiLCJ0aWQiOiI0ZThiZGEwNS02NWRhLTQ5MDAtODc4NS1mMDQxODhjY2Y1NTYiLCJ1bmlxdWVfbmFtZSI6InJpY2thcmQud2luYmVyZ0BQb2xsZXhBQi5vbm1pY3Jvc29mdC5jb20iLCJ1cG4iOiJyaWNrYXJkLndpbmJlcmdAUG9sbGV4QUIub25taWNyb3NvZnQuY29tIiwidXRpIjoiWURqRnFWVXBTa0N1Q3lmM2dNWVBBQSIsInZlciI6IjEuMCIsIndpZHMiOlsiYjc5ZmJmNGQtM2VmOS00Njg5LTgxNDMtNzZiMTk0ZTg1NTA5Il0sInhtc19zdCI6eyJzdWIiOiI3Y09nRmJXcFJycjBMVEgyY21oTU56SUh3Wm80eG12N20wSS1TOW80SE5jIn0sInhtc190Y2R0IjoxNDI1NTY3ODA5LCJ4bXNfdGRiciI6IkVVIn0.XcaduUZsgZA4zV1_NPyephhSyWAErUVBoXU_H0OXbFWXBwfHM0EJ_vw9w23BbxvT6tIy9SumJgAXyZdr5v67FyHYadsgHRbUM2PZwmrr9SvcaeoF0SnXH0Ghzu7KSUzC5Y59zQ7xWcMdxj-pa8HW1TPiExWokerDX8VLpAMR1V12AxOBL_69gwu5FUlQo1FQSlcJBXv9bWjtN3-hu_ac5bnBdCMQfmukh7JmGZEezd2fZjkcKHHhx3miUIglzkpw1tKpELcxmCU3h3uQ6JFU32QYiBL7CxtLBfMIHbE6ujQHHjyRgZkEGRntkAQFB-B6Byzy41DAK6nS4i_lsDlH2g","expires_on":"2023-03-07T20:32:40.8237283Z","id_token":"eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6Ii1LSTNROW5OUjdiUm9meG1lWm9YcWJIWkdldyJ9.eyJhdWQiOiI4MmZiODdiMC0wYWE0LTRlODEtYTcxMy1iZGJhODJmMTcxMzciLCJpc3MiOiJodHRwczovL2xvZ2luLm1pY3Jvc29mdG9ubGluZS5jb20vNGU4YmRhMDUtNjVkYS00OTAwLTg3ODUtZjA0MTg4Y2NmNTU2L3YyLjAiLCJpYXQiOjE2NzgyMTYwMTUsIm5iZiI6MTY3ODIxNjAxNSwiZXhwIjoxNjc4MjE5OTE1LCJhaW8iOiJBVlFBcS84VEFBQUEzVllDRnZhcVdlSUZPcmk3WW16aDAxK0VYdVYvY3pNVUFpbkY4bmJ0NWNNT3E2Vm5SVXVLWkY5T2krTkZwK1dLekFrZTVLNnkrUWgyeDNPcnMyOGRkZUtlMVBaK200WFhRUmd0b0FKWDNwMD0iLCJuYW1lIjoiUmlja2FyZCBXaW5iZXJnIiwibm9uY2UiOiI5YTYzYTliMjkzNTA0MDJhYmU1NGYwMDdlN2VmZTE0MV8yMDIzMDMwNzE5MTYzMyIsIm9pZCI6ImVlYTEzOTZhLWQ4ZWQtNDlmZS1hNmY2LTk0NGZmN2U1MDU2ZCIsInByZWZlcnJlZF91c2VybmFtZSI6InJpY2thcmQud2luYmVyZ0BQb2xsZXhBQi5vbm1pY3Jvc29mdC5jb20iLCJyaCI6IjAuQVRrQUJkcUxUdHBsQUVtSGhmQkJpTXoxVnJDSC00S2tDb0ZPcHhPOXVvTHhjVGM1QUlVLiIsInN1YiI6IjdjT2dGYldwUnJyMExUSDJjbWhNTnpJSHdabzR4bXY3bTBJLVM5bzRITmMiLCJ0aWQiOiI0ZThiZGEwNS02NWRhLTQ5MDAtODc4NS1mMDQxODhjY2Y1NTYiLCJ1dGkiOiJZRGpGcVZVcFNrQ3VDeWYzZ01ZUEFBIiwidmVyIjoiMi4wIn0.jcqLb2GQzNjXKXSH2bucer8Bv8Genttf86NgQm9igq7Xkr16p_1tYtdY2LAYoWg2rxVqe3Dzjy_9oUSGOjj5ErSyyrrPoQHdVOSNoKv_7bUD0zCnsDZ4DGmXgmk_gDA5daxRICYcssQ2GMkpYO25oXIGLAe_5ukXaV2s4WkZaW0u3k4baZfGlcecBvkCv5jF1fB1gFR301YYUueEBoGEnYaBs2t7gJNmWqLvi84RC7Z5N63_9femjlGMh9c6dTPFyWvMvx0YiqPy2rdYnKiMx4ZEWlB84KncvZAfKY1qRvArY8n3TKo79_dja_LK2u1u6ZY7T73fi6TjoWjbiN-vtA","provider_name":"aad","user_claims":[{"typ":"aud","val":"82fb87b0-0aa4-4e81-a713-bdba82f17137"},{"typ":"iss","val":"https:\\/\\/login.microsoftonline.com\\/4e8bda05-65da-4900-8785-f04188ccf556\\/v2.0"},{"typ":"iat","val":"1678216015"},{"typ":"nbf","val":"1678216015"},{"typ":"exp","val":"1678219915"},{"typ":"aio","val":"AVQAq\\/8TAAAASspe9mW8p8O41l+MU07Hx8Y4i6CTwF8j03TWyNBCIdnOzZytwtepQLc7zDYaKkelVSjSg4nyf5guL\\/IqQKcCRAzOIyohwxBbUoJVmNt6wSg="},{"typ":"c_hash","val":"qouhD1did3PaawVh8hFKbQ"},{"typ":"cc","val":"CmB5wgbZ9Bv+SFgC3\\/mN1GDLZbJRh8tc1iQ82G6ZUbuXcen5n0vas+vPHqMYCDmPQBChwYKevItEyK2LD3MdchiNnu01n0Bqx5Pte38987QyYYD0CanRDAcgpeR4k\\/204MwSGHBvbGxleGFiLm9ubWljcm9zb2Z0LmNvbRoSChDt9k4pvqKrTYKQmOkhx4OZIhIKEBmkP+gNSSdOkWAihwvjRgAyAkVVOAFCCQmA5o6dV9zaSA=="},{"typ":"name","val":"Rickard Winberg"},{"typ":"nonce","val":"9a63a9b29350402abe54f007e7efe141_20230307191633"},{"typ":"http:\\/\\/schemas.microsoft.com\\/identity\\/claims\\/objectidentifier","val":"eea1396a-d8ed-49fe-a6f6-944ff7e5056d"},{"typ":"preferred_username","val":"rickard.winberg@PollexAB.onmicrosoft.com"},{"typ":"rh","val":"0.ATkABdqLTtplAEmHhfBBiMz1VrCH-4KkCoFOpxO9uoLxcTc5AIU."},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/nameidentifier","val":"7cOgFbWpRrr0LTH2cmhMNzIHwZo4xmv7m0I-S9o4HNc"},{"typ":"http:\\/\\/schemas.microsoft.com\\/identity\\/claims\\/tenantid","val":"4e8bda05-65da-4900-8785-f04188ccf556"},{"typ":"uti","val":"GaQ_6A1JJ06RYCKHC-NGAA"},{"typ":"ver","val":"2.0"}],"user_id":"Rickard Winberg"}]`;
            let authSampleResponseMsdn = `[{"access_token":"EwCAA8l6BAAUAOyDv0l6PcCVu89kmzvqZmkWABkAAYVYVmF3NTxyW2ZxzfgD4TAVFOA3GXn1qoOfSTs4Ll49Th3KDetJ+k1Qvne05jJtD3zQ\\/GWvytwKvzAfIg7VM\\/mUY86F6Dzvg8LlvQQiQvywsQCdxWo3wsipIgT7IMrLgYg9aHhPEYg8zcVC3wQIMhby3HlZussH23B2u3ApnFtUgHyvJ9rKEumcFS6IqD9I2UECgmKz9nTFa0iFISkvsvbqOGvD5X34qP3kJw9HTs+ElxG8IaKY9cCPkR6ViUoIF+kuevxA1sI1TBMI7yw+j+OTVojFdkhcVZmd1lez3Oj8tnfVLvhOXoni6nq\\/VifB1Cmbd+W8zaxPD+nl\\/\\/2ZDIQDZgAACAjEjZtc3vrlUAK\\/KjUf5pUNXCKNLhXIkv2me3OrZZ63BXI3ibsPD4+O6+m4GkeUGD0WwRuoZ78De7PXe2KoS5tH4Nwtud29U+Y+cnzTLPxNac0KUlcXSUsEj\\/4IRNDWRfB7hp2LDMNp9NL\\/qZV5LmwG\\/lhAZYbaNIA9gCrhfN4i+aZMYiddNSF6dPyZLvVEwzda8MvhmytCnlb4MJXa+NtmvOvCJUSOzXLqmdIw7vrBh6l7YMzK7dhewVFV36nN2d7zcFxvIZQn0SGoOZNuG5xpMI0zDeaJIOvZHRnmLyVob6Rwdr4p+3LjxsXBNCFLWR8ASvCwQzgyIAxX1KgGZsN\\/A99FKcpcwd7sbeJJoiW5jfiHT5HsBrjqbZVuaWmFLcbKXWhb25i5dlLJgjmrC\\/2vDO0RUrN86cFLtI7Z3\\/exSABUgilLwyNdaQvIFOHYmTKeHHjcsm7NELSgl5DeWFY7d0xUu7D9lBhmwWgtc6FMOg4Zrw5D8d\\/0rk2B3ExAMTmeS\\/ZcDs9iwARofzqBY2ej8dhkMl6RuCLv\\/nQTwwRl1RCvGnfbCAvmFjAR+ZhiGzkrUvJI4GguyusexB8IpuX0Mv2SDIxh\\/D7LIznhLTRge6kq7k7kgsaJa1ypQ0JKX+SHIi3BAk2McyUisCKTrXBfZt2Qe+TUiWCBzoruOiAM9GHc4ejZhzMq7SjXNg8FpNSTpm9ls1miqS7iby18pT7\\/aJ3dv0d3eSXhMbAJsN5LlCajDPaZlErjuVdL6Fy4W1ekCRRMm0VNaNcb359yBs9SzrkW\\/MY87w\\/QkwI=","expires_on":"2023-03-07T20:12:50.6847599Z","id_token":"eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6ImJXOFpjTWpCQ25KWlMtaWJYNVVRRE5TdHZ4NCJ9.eyJ2ZXIiOiIyLjAiLCJpc3MiOiJodHRwczovL2xvZ2luLm1pY3Jvc29mdG9ubGluZS5jb20vOTE4ODA0MGQtNmM2Ny00YzViLWIxMTItMzZhMzA0YjY2ZGFkL3YyLjAiLCJzdWIiOiJBQUFBQUFBQUFBQUFBQUFBQUFBQUFJM3NoQ2FueS10NV9wbDA4N0hQdVhFIiwiYXVkIjoiODJmYjg3YjAtMGFhNC00ZTgxLWE3MTMtYmRiYTgyZjE3MTM3IiwiZXhwIjoxNjc4MzAyNzcwLCJpYXQiOjE2NzgyMTYwNzAsIm5iZiI6MTY3ODIxNjA3MCwibmFtZSI6IlJpY2thcmQgV2luYmVyZyIsInByZWZlcnJlZF91c2VybmFtZSI6InJpY2thcmQud2luYmVyZ0Bwcm92ZW1lbnQuc2UiLCJvaWQiOiIwMDAwMDAwMC0wMDAwLTAwMDAtZGVjMi00ODFmNzkzYjNiNmUiLCJlbWFpbCI6InJpY2thcmQud2luYmVyZ0Bwcm92ZW1lbnQuc2UiLCJ0aWQiOiI5MTg4MDQwZC02YzY3LTRjNWItYjExMi0zNmEzMDRiNjZkYWQiLCJub25jZSI6IjZkYzJjYjM0MjZjNTQwZjZhNTFhMDJiNTYyZDhiY2QwXzIwMjMwMzA3MTkxNzMzIiwiYWlvIjoiRGNpa3h3RnJ4ZjRWVHVZMkJUVHkqajBHS3NkY3hTVDNBemtUclRTQUNkWFA2blNnYkM5UkcqclJVcTkqblFtTGRjNk9OeG8wUnJxcTdGbGdDb2t0YjhpTXgzTzVHblgxcjJXTUFjNjI1VWtoNFdNcmhhbWJ0MXQwU0NXT2Q1eHV2YkpXN1U2QjJzV2NKUjJ3REpWZEZVbyQifQ.bY4EqmdBtd7D176ydB9WLed1XYtj7YMAB8NsqyNXMg67KECGKrHgIv8L8M7G-Yf0CuTX2f-Za4a9pzl6Oqxt3F8xv4fHHUcC5UGlVd4ro23MP1_VMroUhXMjcNpNHjW6usMCACqDixiA-sb7v9X6pDcuu9viBXt92UcrDYsISclk1WUfweWo1nBguybF5BnYXOPXmpnQaUj2_EXHbzIXXtn7Sawkv4xa9aULnRyBItjR3cO7w88FpqT8VrKdvoNiFgPFqQDEBMCveL6YxG3CzcS-J2wRyQf75cBaE0Hd-DynjzSFyGU3c-LUx9uNyFz4Talz4J4hVyW3TrwOySoVKQ","provider_name":"aad","user_claims":[{"typ":"ver","val":"2.0"},{"typ":"iss","val":"https:\\/\\/login.microsoftonline.com\\/9188040d-6c67-4c5b-b112-36a304b66dad\\/v2.0"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/nameidentifier","val":"AAAAAAAAAAAAAAAAAAAAAI3shCany-t5_pl087HPuXE"},{"typ":"aud","val":"82fb87b0-0aa4-4e81-a713-bdba82f17137"},{"typ":"exp","val":"1678302770"},{"typ":"iat","val":"1678216070"},{"typ":"nbf","val":"1678216070"},{"typ":"name","val":"Rickard Winberg"},{"typ":"preferred_username","val":"rickard.winberg@provement.se"},{"typ":"http:\\/\\/schemas.microsoft.com\\/identity\\/claims\\/objectidentifier","val":"00000000-0000-0000-dec2-481f793b3b6e"},{"typ":"http:\\/\\/schemas.xmlsoap.org\\/ws\\/2005\\/05\\/identity\\/claims\\/emailaddress","val":"rickard.winberg@provement.se"},{"typ":"http:\\/\\/schemas.microsoft.com\\/identity\\/claims\\/tenantid","val":"9188040d-6c67-4c5b-b112-36a304b66dad"},{"typ":"c_hash","val":"0TNxPhq9lrCDc3z49Dqhtw"},{"typ":"nonce","val":"6dc2cb3426c540f6a51a02b562d8bcd0_20230307191733"},{"typ":"aio","val":"DSKh3Fuz0sq6eO91T98ePKaLPgeHLvxosH4q3LOwBCURIAvNpMM4WiDz!yLrIsjU3pLdFOolGXQTDecS23G59lwYbKNw4xauA!1DDpfNKp4UipQUCTxgv0rvQExgfU79iHUKtDI8X*h5b64tJxY7!u0$"}],"user_id":"rickard.winberg@provement.se"}]`;
            dispatch({type: requestUserAuthType});
            dispatch({type: receiveUserAuthType, userAuths: JSON.parse(authSampleResponsePollex)});
            dispatch(actionCreators.requestHistory(config.historyMaxCount, config.historyMinVersion, null))
            dispatch(actionCreators.requestAdminHistory(config.historyMaxCount, config.historyMinVersion, null))
            return;
        }
        
        dispatch({ type: requestUserAuthType });

        // Use to simulate network error
        const url = `/.auth/me`;
        // const url = `https://wa-prod-cmn-climatecalc.ase02.ncc.info/.auth/me`;

        try {
            
            const rawResponse = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (rawResponse.ok) {
                let userAuths = await rawResponse.json();
                dispatch({type: receiveUserAuthType, userAuths});
                // receiveUserAuthType will reset history, always reload history after auth
                dispatch(actionCreators.requestHistory(config.historyMaxCount, config.historyMinVersion, null))
                dispatch(actionCreators.requestAdminHistory(config.historyMaxCount, config.historyMinVersion, null))
                history.push('/calculation');
            } else {
                // Network errors not handled here, this is for example http 404 or 500 error response
                let responseText = await rawResponse.text();
                let errorMsg = `Received error response from authentication info service. Status code ${rawResponse.status}. Response text: ${responseText}`;
                console.error(errorMsg);
            }

        } catch(e) {
            // Network error for example
            let errorMsg = `Probable network error when calling authentication info service. Exception: ${e.toString()}`;
            console.error(errorMsg);
        }


    },

    requestHistory: (maxCount: number, minVersion: string, before: ?string) => async (dispatch: Dispatch, getState: GetState) => {
        
        dispatch({ type: requestHistoryType });

        let url = `${Config.urlHistory}?maxCount=${maxCount}&minVersion=${minVersion}`;
        if(before)
            url += `&before=${before}`;

        try {
            
            const rawResponse = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (rawResponse.ok) {
                let calcHistory = await rawResponse.json();
                
                if(calcHistory.length < maxCount)
                    dispatch({type: allHistoryLoadedType});                 
                
                let prevState = getState();
                // $FlowFixMe
                if(prevState && prevState.calculation.history)
                    calcHistory = [...prevState.calculation.history, ...calcHistory];
                dispatch({type: receiveHistoryType, history: calcHistory});
            } else {
                // Network errors not handled here, this is for example http 404 or 500 error response
                let responseText = await rawResponse.text();
                let errorMsg = `Received error response from history service. Status code ${rawResponse.status}. Response text: ${responseText}`;
                console.error(errorMsg);
            }

        } catch(e) {
            // Network error for example
            let errorMsg = `Probable network error when calling history service. Exception: ${e.toString()}`;
            console.error(errorMsg);
        }


    },

    requestAdminHistory: (maxCount: number, minVersion: string, before: ?string) => async (dispatch: Dispatch, getState: GetState) => {
        
        dispatch({ type: requestAdminHistoryType });

        let url = `${Config.urlHistory}?maxCount=${maxCount}&minVersion=${minVersion}&allUsers=1`;
        if(before)
            url += `&before=${before}`;

        try {
            
            const rawResponse = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (rawResponse.ok) {
                let calcHistory = await rawResponse.json();
                
                if(calcHistory.length < maxCount)
                    dispatch({type: allAdminHistoryLoadedType});                 
                
                let prevState = getState();
                // $FlowFixMe
                if(prevState && prevState.calculation.adminHistory)
                    calcHistory = [...prevState.calculation.adminHistory, ...calcHistory];
                dispatch({type: receiveAdminHistoryType, adminHistory: calcHistory});
            } else {
                // Network errors not handled here, this is for example http 404 or 500 error response
                let responseText = await rawResponse.text();
                let errorMsg = `Received error response from history service (admin). Status code ${rawResponse.status}. Response text: ${responseText}`;
                console.error(errorMsg);
            }

        } catch(e) {
            // Network error for example
            let errorMsg = `Probable network error when calling history service (admin). Exception: ${e.toString()}`;
            console.error(errorMsg);
        }


    },

    addToHistory(calcRequestInfo: CalculationRequestInfo) : AddToHistoryAction {
        return {
            type: addToHistoryType,
            calcRequestInfo
        };
    },
    
    addToAdminHistory(calcRequestInfo: CalculationRequestInfo) : AddToHistoryAction {
        return {
            type: addToAdminHistoryType,
            calcRequestInfo
        };
    },
    
};

function createCalcRequestInfo(receiveCalcAction: ReceiveCalculationAction): CalculationRequestInfo {
    return {
        _id: '',
        userEmail: '',
        userGuid: '',
        host: '',
        requestTimeUtc: '',
        requestTimeLocal: new Date().toLocaleString('se-SV'),
        requestTimeMs: 0,
        backendVersion: '',
        frontendVersion: receiveCalcAction.calcInput.frontEndVersion,

        requestInput: receiveCalcAction.calcInput,
        requestResult: receiveCalcAction.calcResult,
    }
}

function getUserNameFromUserAuth(userAuth: UserAuth) {
    let name = "";
    let givenname = "";
    let surname = "";
    if(Array.isArray(userAuth.user_claims)) {
        for(let j = 0; j < userAuth.user_claims.length; j++) {
            if(userAuth.user_claims[j].typ === 'name')
                name = userAuth.user_claims[j].val;
            if(userAuth.user_claims[j].typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname')
                givenname = userAuth.user_claims[j].val;
            if(userAuth.user_claims[j].typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname')
                surname = userAuth.user_claims[j].val;
        }
    }
    if(givenname && surname)
        return `${givenname} ${surname}`;
    else if(name)
        return name;
    else
        return "";
}

function getUserNameFromUserAuths(userAuths: UserAuth[]) {
    for(let i = 0; i < userAuths.length; i++) {
        const name = getUserNameFromUserAuth(userAuths[i]);
        if(name) {
            return name;
        }
    }
    return "";
}

function getOrganisationNameFromUserAuths(userAuths: UserAuth[]) {
    let email = getEmailFromUserAuths(userAuths);
    if(email.endsWith("pollex.se") || email.endsWith("PollexAB.onmicrosoft.com")) {
        return "Pollex AB";
    } else if (email.endsWith("provement.se")) {
        return "Provement AB";
    }
    return "";
}

function getEmailFromUserAuth(userAuth: UserAuth) {
    if(userAuth.user_id.includes("@")) {
        return userAuth.user_id;
    } else if(Array.isArray(userAuth.user_claims)) {
        for(let j = 0; j < userAuth.user_claims.length; j++) {
            if(userAuth.user_claims[j].typ === 'preferred_username'
                && userAuth.user_claims[j].val.includes("@")) {
                return userAuth.user_claims[j].val;
            }
        }
    }
    return "";
}

function getEmailFromUserAuths(userAuths: UserAuth[]) {
    for(let i = 0; i < userAuths.length; i++) {
        const email = getEmailFromUserAuth(userAuths[i]);
        if(email) {
            return email;
        }
    }
    return "";
}


function getUserAuthsSummary(userAuths: UserAuth[]) {
    if(userAuths && userAuths.length) {
        return userAuths.map(ua => ({
            providerName: ua.provider_name,
            userId: ua.user_id,
            userClaims: ua.user_claims,
            userEmail: getEmailFromUserAuth(ua),
            userRealName: getUserNameFromUserAuth(ua)
        }));
    } else {
        return null;
    }
}

export const reducer = (state: CalculationReduxState, action: Action): CalculationReduxState => {
    state = state || initialState;
    
    if (action.type === requestCalculationType) {
        return {
            ...state,
            calcInput: action.calcInput,
            isLoading: true,
            calcErrorCode: 0,
            calcErrorString: ""
        };
    }

    if (action.type === receiveCalculationType) {
        return {
            ...state,
            calcInput: action.calcInput,
            calcResult: action.calcResult,
            isLoading: false,
            calcErrorCode: 0,
            calcErrorString: ""
        };
    }
    
    if (action.type === receiveCalculationErrorType) {
        return {
            ...state,
            calcResult: null,
            isLoading: false,
            calcErrorCode: action.errorCode,
            calcErrorString: action.errorString
        };
    }

    if (action.type === requestUserAuthType) {
        return {
            ...state,
            userAuthsIsLoading: true,
        };
    }

    if (action.type === receiveUserAuthType) {
        let personName = getUserNameFromUserAuths(action.userAuths);
        let personEmail = getEmailFromUserAuths(action.userAuths);
        let organisationName = getOrganisationNameFromUserAuths(action.userAuths);
        ReactAI.setAppContext({
            urlReferrer: document.referrer,
            uiVersion: Config.version,
            personName,
            personEmail,
            organisationName
        });
        return {
            ...state,
            userAuths: action.userAuths,
            userAuthsIsLoading: false,
            projectUiInput: {...state.projectUiInput, 
                personName,
                organisationName
            },
            personEmail,
            authSummary: getUserAuthsSummary(action.userAuths),
            history: null // Always reset history after new login
        };
    }

    if (action.type === requestHistoryType) {
        return {
            ...state,
            historyIsLoading: true,
        };
    }

    if (action.type === receiveHistoryType) {
        return {
            ...state,
            history: action.history,
            historyIsLoading: false,
        };
    }

    if (action.type === requestAdminHistoryType) {
        return {
            ...state,
            adminHistoryIsLoading: true,
        };
    }

    if (action.type === receiveAdminHistoryType) {
        return {
            ...state,
            adminHistory: action.adminHistory,
            adminHistoryIsLoading: false,
        };
    }

    if (action.type === changePhaseValuesType) {
        let phaseNr = action.phaseNr;
        let partialPhaseState = action.partialPhaseState;
        
        if(phaseNr > 0) {
            let phaseStates = _.range(phaseNr - 1).map(() => ({}));
            phaseStates.push(partialPhaseState);
            return _.merge({}, state, {projectUiInput: {phaseStates}});
        }
        else {
            // phaseNr === 0 is used to modify project info
            return _.merge({}, state, {projectUiInput: {...partialPhaseState}});
        }
    }

    if (action.type === addPhaseType) {
        return {
            ...state,
            projectUiInput: {
                ...state.projectUiInput,
                phaseStates: [...state.projectUiInput.phaseStates, defaultValueManager.getDefaultState()]
            }
        };
    }

    if (action.type === removePhaseType) {
        let newStates = [...state.projectUiInput.phaseStates];
        newStates.splice(action.phaseNr - 1, 1);
        return {
            ...state,
            projectUiInput: {
                ...state.projectUiInput,
                phaseStates: newStates
            }
        };
    }

    if (action.type === clearCalculationResultType) {
        return {
            ...state,
            calcResult: null,
            calcErrorCode: 0,
            calcErrorString: ""
        };
    }

    if (action.type === setCalcParamsType) {
        return {
            ...state,
            projectUiInput: action.params
        };
    }

    if (action.type === addToHistoryType) {
        return {
            ...state,
            history: state.history ? [action.calcRequestInfo, ...state.history] : [action.calcRequestInfo]
        };
    }

    if (action.type === allHistoryLoadedType) {
        return {
            ...state,
            allHistoryLoaded: true
        };
    }

    if (action.type === addToAdminHistoryType) {
        return {
            ...state,
            adminHistory: state.adminHistory ? [action.calcRequestInfo, ...state.adminHistory] : [action.calcRequestInfo]
        };
    }

    if (action.type === allAdminHistoryLoadedType) {
        return {
            ...state,
            allAdminHistoryLoaded: true
        };
    }

    return state;
};


// WEBPACK FOOTER //
// ./src/store/Calculation.js